import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import InfoDisplay from "@/components/InfoDisplay";
import type { FlatRateAgreement, Applicant, Consumer } from "../types";
import Layout from "../../shared/components/Layout";
import { formatDate, formatMonth } from '@/utils/format';
import { CustomerParams } from "../types";
import updateCustomoer from "../api/updateCustomer";
import { AddressFieldBox } from "@/components/AddressField";
import { PhoneFieldBox } from "@/components/PhoneField";
import OccupationLabels from "@/features/customer/shared/types/OccupationLabels";
import SexLabels from "@/features/customer/shared/types/SexLabels";
import { ChangePlanRequest } from "../types";
import clearLocalStorage from "@/utils/clearLocalStorage";
import SidebarNavigation from "@/components/SidebarNavigation";

const CustomerConfirm: React.FC = () => {
  const displayPlan = (plan: string, price: string): string => {
    if(plan === 'both') return `両眼プラン(${price}円/月)`
    if(plan === 'right') return `片眼プラン(右眼)(${price}円/月)`
    if(plan === 'left') return `片眼プラン(左眼)(${price}円/月)`
    return '';
  }

  const handleButtonClick = (orderId: string | undefined, event: React.MouseEvent<HTMLButtonElement>): void => {
    if(!orderId) {
      const confirmRegistration = window.confirm('新規契約対象のレンズ注文が存在しませんが、登録しますか？');
      if (!confirmRegistration) {
        event.preventDefault();
      }
    }
  };

  const OccupationLabels: OccupationLabels = {
    employee: "会社員・公務員",
    independent: "自営",
    housewife: "主婦",
    student: "学生",
    other: "その他"
  }

  const SexLabels: SexLabels = {
    male: "男性",
    female: "女性",
    unknown: "不明",
  }

  const navigate = useNavigate();

  const [customer, setCustomer] = useState({
    custCode: "",
    email: "",
  });
  const [orderId, setOrderId] = useState<string>();

  const [flatRateAgreement, setFlatRateAgreement] = useState<FlatRateAgreement>();
  const [applicant, setApplicant] = useState<Applicant>({
    address1: "",
    address2: "",
    birthday: "",
    homePhone: "",
    mobilePhone: "",
    name: "",
    nameKana: "",
    occupation: "",
    sex: "",
    stateId: "",
    zipcode: "",
  });
  const [consumer, setConsumer] = useState<Consumer>({
    address1: "",
    address2: "",
    birthday: "",
    homePhone: "",
    mobilePhone: "",
    name: "",
    nameKana: "",
    occupation: "",
    relationship: "",
    sex: "",
    stateId: "",
    zipcode: "",
    isSameApplicant: true,
    isSameAddress: true,
    isSamePhone: true
  });

  const[changePlanRequest, setChangePlanRequest] = useState<ChangePlanRequest>({
    afterPlan: "",
    afterPrice: "",
    changeMonth: "",
    isChangePlan: false,
    lensPriceId: ""
  })
  
  const [states, setStates] = useState([]);
  const [usageFeeOccurMonth, setUsageFeeOccurMonth] = useState<string>("");
  const [usageFeeFirstPaymentMonth, setUsageFeeFirstPaymentMonth] = useState<string>("");

  useEffect(() => {
    const storedCustomer = localStorage.getItem("customer");
    if(storedCustomer) {
      setCustomer(JSON.parse(storedCustomer));
    }

    const storedApplicant = localStorage.getItem("applicant");
    if(storedApplicant) {
      setApplicant(JSON.parse(storedApplicant));
    }

    const storedConsumer = localStorage.getItem("consumer");
    if(storedConsumer) {
      setConsumer(JSON.parse(storedConsumer));
    }

    const storedFlatRateAgreement = localStorage.getItem("flatRateAgreement");
    if(storedFlatRateAgreement) {
      setFlatRateAgreement(JSON.parse(storedFlatRateAgreement));
    }

    const storedChangePlanRequest = localStorage.getItem("changePlanRequest");
    if(storedChangePlanRequest) {
      setChangePlanRequest(JSON.parse(storedChangePlanRequest));
    }

    const storedStates = localStorage.getItem("states");
    if(storedStates) {
      setStates(JSON.parse(storedStates));
    }

    const storedUsageFeeOccurMonth = localStorage.getItem("usageFeeOccurMonth");
    if(storedUsageFeeOccurMonth) {
      setUsageFeeOccurMonth(JSON.parse(storedUsageFeeOccurMonth));
    }

    const storedUsageFeeFirstPaymentMonth = localStorage.getItem("usageFeeFirstPaymentMonth");
    if(storedUsageFeeFirstPaymentMonth) {
      setUsageFeeFirstPaymentMonth(JSON.parse(storedUsageFeeFirstPaymentMonth));
    }

    const storedOrderId = localStorage.getItem("orderId");
    if(storedOrderId) {
      setOrderId(storedOrderId);
    }
  }, []);

  const applicantAddressData = {
    zipcode: {
      label: "郵便番号",
      value: applicant.zipcode,
      onChange: undefined
    },
    stateId: {
      label: "都道府県",
      value: applicant.stateId,
      onChange: undefined
    },
    address1: {
      label: "市区町村",
      placeholder: "",
      value: applicant.address1,
      onChange: undefined
    },
    address2: {
      label: "建物名",
      placeholder: "",
      value: applicant.address2,
      onChange: undefined
    },
  }

  const consumerAddressData = {
    zipcode: {
      label: "郵便番号",
      value: consumer.zipcode,
      onChange: undefined
    },
    stateId: {
      label: "都道府県",
      value: consumer.stateId,
      onChange: undefined
    },
    address1: {
      label: "市区町村",
      placeholder: "",
      value: consumer.address1,
      onChange: undefined
    },
    address2: {
      label: "建物名",
      placeholder: "",
      value: consumer.address2,
      onChange: undefined
    },
  }

  const handleSubmit = async(e: React.MouseEvent<HTMLFormElement>) => {
    e.preventDefault()

    const params: CustomerParams = {
      changePlanRequest: {...changePlanRequest, orderId: orderId},
      applicant: applicant,
      consumer: consumer,
    }

    try {
      const res = await updateCustomoer(params, customer.custCode);
      if(res.status === 201) {
        clearLocalStorage();
        navigate(`/facility/flat_rates/customers/${customer.custCode}/complete`)
      } else if(res.status !== 201) {
        navigate(`/facility/flat_rates/customers/${customer.custCode}`);
        window.scrollTo(0, 0);
        console.error(`status: ${res.status}, message: ${res.statusText}`);
      }
    } catch (err: any) {
      console.log('An unexpected error occurred:', err.message);
    }
  }

  const renderPreviousPage = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigate(`/facility/flat_rates/customers/${customer.custCode}`, { state: { fromConfirmPage: true } });
    window.scrollTo(0, 0);
  }

  return(
    <Layout>
      <main id="page-user_index" className="wrapper">
        <section className="sec-contact">
          <div className="inner">
            <h1 className="c-ttl__01">お客様登録情報変更入力内容確認</h1>
            <div className="contact-box">
              <div className="contact-box__left">
                <form onSubmit={handleSubmit}>
                  <div className="contact-box__form js-item" id="id-01">
                    <h2 className="c-ttl__02">お客様情報</h2>
                    <InfoDisplay label={"契約種別"} value={"定額制契約"} />
                    <InfoDisplay label={"お客様ID"} value={customer.custCode} />
                  </div>
                  <div className="contact-box__form mbsp-29 js-item h-adr" id="id-02">
                    <h2 className="c-ttl__02">申込者情報</h2>
                    <InfoDisplay label="申込者名(カナ)" value={applicant.nameKana} />
                    <InfoDisplay label="申込者名(漢字)" value={applicant.name} />
                    <InfoDisplay label="申込者性別" value={applicant.sex ? SexLabels[applicant.sex] : ""} />
                    <InfoDisplay label="申込者生年月日" value={formatDate(applicant.birthday)} />
                    <AddressFieldBox
                      label="申込者住所"
                      addressData={applicantAddressData}
                      required={false}
                      states={states}
                      inputAddressData={undefined}
                    />
                    <PhoneFieldBox
                      label="申込者電話番号"
                      phoneData={{
                        homePhone: {label: "自宅", value: applicant.homePhone, onChange: undefined},
                        mobilePhone: {label: "携帯", value: applicant.mobilePhone, onChange: undefined},
                      }}
                      required={false}
                    />
                    <InfoDisplay label="申込者ご職業" value={applicant.occupation ? OccupationLabels[applicant.occupation] : ""} />
                  </div>
                  <div className="contact-box__form mbsp-29 js-item h-adr" id="id-03">
                    <h2 className="c-ttl__02">レンズ使用者情報</h2>
                    <InfoDisplay label="使用者について" value={consumer.isSameApplicant ? "申込者と同一" : "申込者と異なる"} />
                    { !consumer.isSameApplicant &&
                      <>
                        <InfoDisplay label="使用者名(カナ)" value={consumer.nameKana} />
                        <InfoDisplay label="使用者名(漢字)" value={consumer.name} />
                        <InfoDisplay label="申込者との関係" value={consumer.relationship} />
                        <InfoDisplay label="使用者性別" value={consumer.sex ? SexLabels[consumer.sex] : ""} />
                        <InfoDisplay label="使用者生年月日" value={formatDate(consumer.birthday)} />
                        { consumer.isSameAddress ?
                          <InfoDisplay label="使用者住所" value={"申込者と同一"} />
                        :
                          <AddressFieldBox
                            label="使用者住所"
                            addressData={consumerAddressData}
                            required={false}
                            states={states}
                            inputAddressData={undefined}
                          />
                        }

                        { consumer.isSamePhone ?
                          <InfoDisplay label="使用者電話番号" value={"申込者と同一"} />
                        :
                          <PhoneFieldBox
                            label="使用者電話番号"
                            phoneData={{
                              homePhone: {label: "自宅", value: consumer.homePhone, onChange: undefined},
                              mobilePhone: {label: "携帯", value: consumer.mobilePhone, onChange: undefined},
                            }}
                            required={false}
                          />
                        }
                        <InfoDisplay label="申込者ご職業" value={consumer.occupation ? OccupationLabels[consumer.occupation] : ""} />
                      </>
                    }
                  </div>
                  <div className="contact-box__form mbsp-29 js-item h-adr" id="id-04">
                    <h2 className="c-ttl__02">契約詳細</h2>
                    <div className="contact-box__item">
                      <label className="contact-box__ttl">
                        <span>ご契約プラン</span>
                      </label>
                      <div className="contact-box__ct">
                        {changePlanRequest.isChangePlan ?
                          <>
                            <p className="contact-box__desc mbpc-5 mbsp-5">{displayPlan(flatRateAgreement?.plan ?? '', flatRateAgreement?.price?.toString() ?? '')}</p> 
                            <p className="contact-box__desc">
                              {`→ ${displayPlan(changePlanRequest?.afterPlan ?? '', changePlanRequest?.afterPrice.toString() ?? '')}`}
                              <br />
                              {`プラン変更月: ${formatMonth(changePlanRequest?.changeMonth ?? '')}`}
                            </p>
                          </>
                        :
                          <p className="contact-box__desc">{displayPlan(flatRateAgreement?.plan ?? '', flatRateAgreement?.price?.toString() ?? '')}</p>
                        }
                      </div>
                    </div>
                    <InfoDisplay label={"サービス利用開始日"} value={formatDate(flatRateAgreement?.startDate)} />
                    <InfoDisplay label="サービス利用開始月" value={formatMonth(flatRateAgreement?.startMonth ?? '')} />
                    <InfoDisplay label="利用料請求発生月" value={usageFeeOccurMonth} />
                    <InfoDisplay label="利用料初回決済月（月末）" value={usageFeeFirstPaymentMonth} />
                    <InfoDisplay label="レンズ注文" value={orderId ? "あり" : "なし"} />
                  </div>
                  <button type="submit" className="c-btn__01" onClick={(event) => handleButtonClick(orderId, event)}>この内容で登録する</button>
                </form>
                <div className="c-al--c">
                  <button className="c-btn__02" onClick={renderPreviousPage}>前のページに戻る</button>
                </div>
              </div>
              <SidebarNavigation items={[
                { id: "id-01", label: "お客様情報" },
                { id: "id-02", label: "申込者情報" },
                { id: "id-03", label: "使用者情報" },
                { id: "id-04", label: "契約詳細" },
                ]} />
            </div>
          </div>
        </section>
      </main>
    </Layout>
  )
};

export default CustomerConfirm;